import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Plus } from 'lucide-react'
import { ApiError, apiClient } from '~/api/client'
import {
    bulkDocuments,
    downloadDocument,
    setCurationLock,
    uploadDocument,
    type DocumentBulkAction,
} from '~/api/documents'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { DocumentDetailPanel } from '~/components/knowledge/DocumentDetailPanel'
import {
    UploadManagementSection,
    type DocumentRow,
} from '~/components/knowledge/UploadManagementSection'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { useToast } from '~/components/ui/toast'
import { useTableSelection } from '~/lib/use-table-selection'

// 업로드 관리: 문서 적재 파이프라인(Postgres documents) 전담 — 문서 추가 · 인덱싱 상태
// KPI/분포 · 재색인 · 삭제 · 일괄작업 · 원본 상세. 실제로 색인되어 검색에 쓰이는 코퍼스는
// 「지식베이스」에서 문서별 상태와 함께 본다(진행 중·실패는 이 페이지에만 나타난다).
export function UploadsPage() {
    const toast = useToast()

    const [docs, setDocs] = useState<DocumentRow[]>([])
    const [loading, setLoading] = useState(true)
    const [q, setQ] = useState('')
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pendingDelete, setPendingDelete] = useState<DocumentRow | null>(null)

    // 업로드 문서 행 클릭 → 원본 파일 정보·다운로드 상세 패널.
    const [selectedUploadDoc, setSelectedUploadDoc] =
        useState<DocumentRow | null>(null)

    // 다중선택(범용 훅) + 일괄 재색인/삭제. 삭제는 적용 전 ConfirmDialog로 확인받는다.
    const selection = useTableSelection<string>()
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
    const [bulkBusy, setBulkBusy] = useState(false)

    // 검색어(제목·도메인 ILIKE)는 쿼리스트링으로 전달한다(빈값·공백이면 생략).
    const load = () => {
        setLoading(true)
        const trimmed = q.trim()
        const query = trimmed ? `?q=${encodeURIComponent(trimmed)}` : ''
        apiClient
            .get<DocumentRow[]>(`/documents${query}`)
            .then(setDocs)
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setLoading(false))
    }

    // 마운트 + 검색어 변경 시 재조회한다.
    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q])

    // 문서 추가: 파일 선택 → 원본 등록(uploadDocument). 실제 청크 적재는 워커가 비동기로 한다.
    const onUploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // 같은 파일 재선택 허용
        if (!file) return
        setUploading(true)
        try {
            const title = file.name.replace(/\.[^.]+$/, '')
            await uploadDocument(file, { title, domain: 'general' })
            toast.success(
                `'${title}' 문서를 추가했어요. 워커가 인덱싱하면 코퍼스에 반영됩니다.`,
                { action: { label: '목록 새로고침', onClick: load } }
            )
            load()
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setUploading(false)
        }
    }

    const reindex = async (id: string) => {
        try {
            await apiClient.post(`/documents/${id}/reindex`)
            toast.success('재색인을 시작했어요.')
            load()
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    // 수동 관리 잠금 토글: 서버가 metadata 플래그를 durable하게 보관한다. 성공하면 목록·열린
    // 상세 패널의 curationLocked를 갱신해 재색인 버튼·토글이 즉시 반영되게 한다.
    const toggleLock = async (id: string, locked: boolean) => {
        try {
            const updated = await setCurationLock(id, locked)
            setDocs((prev) =>
                prev.map((d) =>
                    d.id === id
                        ? { ...d, curationLocked: updated.curationLocked }
                        : d
                )
            )
            setSelectedUploadDoc((prev) =>
                prev && prev.id === id
                    ? { ...prev, curationLocked: updated.curationLocked }
                    : prev
            )
            toast.success(
                locked
                    ? '수동 관리 잠금을 켰어요. 재색인이 이 문서를 건너뜁니다.'
                    : '수동 관리 잠금을 해제했어요.'
            )
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    const confirmRemove = async () => {
        if (!pendingDelete) return
        const { id, title } = pendingDelete
        setPendingDelete(null)
        try {
            await apiClient.delete(`/documents/${id}`)
            toast.success(`'${title}' 문서를 삭제했어요.`)
            load()
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    // 일괄 처리: 선택 ids 전체에 action 적용 → 결과 토스트 + 목록 새로고침 + 선택 해제.
    const runBulk = async (action: DocumentBulkAction) => {
        const ids = [...selection.selected]
        if (ids.length === 0) return
        setBulkBusy(true)
        try {
            const r = await bulkDocuments(ids, action)
            selection.clear()
            load()
            const verb = action === 'reindex' ? '재색인' : '삭제'
            toast.success(
                `${verb} 완료 · 처리 ${r.processed} · 실패 ${r.failed.length}`,
                { action: { label: '목록 새로고침', onClick: load } }
            )
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBulkBusy(false)
        }
    }

    const confirmBulkDelete = () => {
        setBulkDeleteConfirm(false)
        runBulk('delete')
    }

    // 원본 다운로드(어드민 백엔드 프록시). 상세 패널이 쓴다. 원본이 없으면(스토리지 미존재)
    // 404 → 크래시 대신 토스트로 안내한다.
    const downloadOriginal = async (id: string, fallbackName: string) => {
        try {
            await downloadDocument(id, fallbackName)
        } catch (e) {
            const notFound = e instanceof ApiError && e.status === 404
            toast.error(
                notFound ? '원본을 찾을 수 없어요.' : (e as Error).message
            )
        }
    }

    return (
        <div>
            <PageHeader
                title='업로드 관리'
                description='문서 적재 파이프라인 · 인덱싱 상태 · 재색인 · 삭제'
                actions={
                    <div className='flex items-center gap-3'>
                        <Link
                            to='/content/knowledge'
                            className='text-primary text-[13px] font-medium underline-offset-2 hover:underline'>
                            ← 지식베이스
                        </Link>
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            title='PDF·Markdown·Excel 원본 파일을 선택해 문서로 등록해요. 워커가 인덱싱하면 코퍼스에 반영됩니다.'>
                            {uploading ? (
                                <Loader2 className='animate-spin' />
                            ) : (
                                <Plus />
                            )}
                            {uploading ? '추가하는 중…' : '문서 추가'}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='.pdf,.md,.markdown,.xlsx,application/pdf,text/markdown,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            className='hidden'
                            onChange={onUploadFile}
                        />
                    </div>
                }
            />

            <UploadManagementSection
                docs={docs}
                loading={loading}
                q={q}
                onQChange={setQ}
                selection={selection}
                bulkBusy={bulkBusy}
                onReindex={reindex}
                onDelete={setPendingDelete}
                onSelect={setSelectedUploadDoc}
                onBulkReindex={() => runBulk('reindex')}
                onRequestBulkDelete={() => setBulkDeleteConfirm(true)}
            />

            <ConfirmDialog
                open={pendingDelete !== null}
                title='문서 삭제'
                description={
                    pendingDelete
                        ? `'${pendingDelete.title}' 문서를 삭제할까요? 색인된 청크도 함께 제거돼요.`
                        : undefined
                }
                confirmLabel='삭제'
                danger
                onConfirm={confirmRemove}
                onCancel={() => setPendingDelete(null)}
            />
            <ConfirmDialog
                open={bulkDeleteConfirm}
                title='문서 일괄 삭제'
                description={`선택한 ${selection.count}개 문서를 삭제할까요? 색인된 청크도 함께 제거돼요.`}
                confirmLabel='삭제'
                danger
                onConfirm={confirmBulkDelete}
                onCancel={() => setBulkDeleteConfirm(false)}
            />

            {/* 업로드 문서 행 클릭 → 원본 파일 정보·다운로드 상세 패널 */}
            {selectedUploadDoc ? (
                <DocumentDetailPanel
                    doc={selectedUploadDoc}
                    onClose={() => setSelectedUploadDoc(null)}
                    onDownload={() =>
                        downloadOriginal(
                            selectedUploadDoc.id,
                            selectedUploadDoc.title
                        )
                    }
                    onToggleLock={(locked) =>
                        toggleLock(selectedUploadDoc.id, locked)
                    }
                />
            ) : null}
        </div>
    )
}
